// @ts-ignore
import React, { useState } from "react";
import cropr, { BackboneModel, CropField, CropFieldModel, Model } from "../../shared/utils/cropr";
import { gettext, View } from "../../shared/utils/legacy";
import { Modal, Tab } from "react-bootstrap";
import GeometryModelPreview from "../../shared/components/GeometryModelPreview";
import LabeledField from "../../shared/forms/LabeledField";
import LabeledSelect from "../../shared/forms/LabeledSelect";
import ModalHeader from "../../shared/components/ModalHeader";
import useCroprBackbone from "../../shared/hooks/useCroprBackbone";
import { getSoilOptions, getSoilTriangles } from "../../shared/utils/soil";
import GeneralError from "../../shared/forms/GeneralError";
import SoilTriangleCroprCodes from "../../shared/components/SoilTriangleCroprCodes";
import ModelEditModalFooter from "../../shared/components/ModelEditModalFooter";
import { ModelSubmitFormik, PropertiesModalProps } from "../../shared/utils/modals";
import CropShort from "./CropShort";
import BackboneComponent from "../../shared/components/BackboneComponent";
import FileOverview from "../../fileupload/FileOverview";
import { convertAttachmentIds, getAttachmentIds } from "../../fileupload/utils";

const CropFieldPropertiesModal = ({
  model,
  onSave,
  onHide,
}: PropertiesModalProps<CropFieldModel>) => {
  const [show, setShow] = useState(true);
  const [contacts] = useCroprBackbone("farm_contacts");
  const typeName = gettext("cropfield");

  // @ts-ignore
  const coWorkerView: any = cropr.NewCoWorkerView;

  function hide() {
    if (coWorkerView.success) coWorkerView.closePopOver();
    setShow(false);
    onHide?.();
  }
  const attachments = convertAttachmentIds(model.get("attachments"));
  const initialValues: CropField = {
    notes: model.get("notes") || "",
    period_end: model.get("period_start"),
    period_start: model.get("period_end"),
    name: model.get("name") || Model.Parcel.generateName(),
    registered_area: model.get("new_area") || model.get("registered_area") || "",
    owner: model.get("owner"),
    soil_type: model.get("soil_type"),
    regulatory_soil_type: model.get("regulatory_soil_type"),
    attachments: model.get("attachments"),
    geometry: model.get("geometry") || model.get("addshape_geometry"),
    field_attributes: model.get("field_attributes"),
    certificates: model.get("certificates"),
  };

  const contactOptions = contacts
    .filter((fc: any) => (fc.is_owner && !fc.deleted) || model.get("owner") === fc.id)
    .map((fc: any) => ({
      value: fc.id,
      label: fc.name,
    }));
  const props = {
    hide,
    typeName,
    model,
    permission: cropr.permissions.crop_f,
    collection: cropr.cropfields,
  };
  return (
    <Modal
      size="xl"
      className="modal-fh dfi-modal"
      show={show}
      onHide={hide}
      backdrop="static"
      enforceFocus={false}
    >
      <ModelSubmitFormik initialValues={initialValues} onSave={onSave} {...props}>
        {({ values, handleSubmit, isSubmitting, setFieldValue, errors }) => (
          <form onSubmit={handleSubmit}>
            <Tab.Container defaultActiveKey="cropfield">
              <ModalHeader
                tabs={[
                  ["cropfield", gettext("Cultivation")],
                  ["attachments", gettext("Attachments")],
                  ["certificates", gettext("Certificates")],
                  ["attributes", gettext("Field attributes")],
                  ["soil_type", gettext("Soil type")],
                  ["soil_research", gettext("Soilresearches")],
                  ["notes", gettext("Notes")],
                ]}
              />
              <Modal.Body>
                <GeneralError errors={errors} />
                <Tab.Content>
                  <Tab.Pane eventKey="cropfield">
                    <div className="row">
                      <div className="col-4 b-r p-4">
                        <h4 className="mb-3">{gettext("Crop field")}</h4>
                        <LabeledField
                          label={gettext("Name")}
                          name="name"
                          type="text"
                          placeholder={gettext("Choose name for field")}
                        />
                        <LabeledField
                          label={`${gettext("Area")} ${cropr.l10n.unit_area_display}`}
                          name="registered_area"
                          placeholder={gettext("Area")}
                          type="text"
                        />
                        <GeometryModelPreview model={model} />
                      </div>
                      <div className="col-4 b-r p-4">
                        <h4 className="mb-3">{gettext("Crop and variety")}</h4>
                        <CropShort />
                        <LabeledSelect
                          label={gettext("Crop")}
                          menuPlacement="auto"
                          options={[]}
                          name="crop"
                          placeholder={gettext("Choose a crop")}
                        />
                        {values["crop"] && (
                          <LabeledSelect
                            label={gettext("Variety")}
                            menuPlacement="auto"
                            options={[]}
                            name="variety"
                            placeholder={gettext("Choose a variety")}
                          />
                        )}
                      </div>
                      <div className="col-4 b-r p-4">
                        <h4 className="mb-3">{gettext("Properties")}</h4>
                        <LabeledSelect
                          label={gettext("Crop purpose")}
                          options={[]}
                          name="purpose"
                          placeholder={gettext("Choose a croppurpose")}
                        />
                        <LabeledSelect
                          label={gettext("Consumer")}
                          options={[]}
                          name="consumer"
                          placeholder={gettext("Choose a consumer")}
                        />
                        <LabeledSelect
                          label={gettext("Contract")}
                          options={[]}
                          name="contract"
                          placeholder={gettext("Choose a contract")}
                        />
                        <div className="form-row">
                          <LabeledField
                            label={gettext("From")}
                            name="period_start"
                            type="date"
                            className="col-sm-6"
                          />
                          <LabeledField
                            label={gettext("To")}
                            name="period_end"
                            type="date"
                            className="col-sm-6"
                          />
                        </div>
                        <LabeledSelect
                          label={gettext("Previous crop")}
                          options={[]}
                          name="previous_crop"
                          placeholder={gettext("Choose a previous crop")}
                        />
                        <LabeledSelect
                          label={gettext("Cover crop")}
                          options={[]}
                          name="cover_crop"
                          placeholder={gettext("Choose a cover crop")}
                        />
                        <LabeledSelect
                          label={gettext("Owner")}
                          id="parcel_modal_owner"
                          name="owner"
                          type={cropr.features.customer_module ? "text" : "hidden"}
                          options={contactOptions}
                          onCreateOption={(inputValue: any) => {
                            coWorkerView.render(
                              {
                                currentTarget: "#cropfield_modal_owner",
                              },
                              inputValue,
                              "owner",
                              undefined,
                              false,
                              (model: any) => setFieldValue("owner", model.id)
                            );
                          }}
                          placeholder={gettext("Choose an owner of this field")}
                        />
                      </div>
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="attachments">
                    <div className="span12">
                      <FileOverview
                        attachments={attachments}
                        permission={cropr.permissions.crop_f}
                        onChange={(attachmentKeys: string[]) =>
                          setFieldValue("attachments", getAttachmentIds(attachmentKeys))
                        }
                      />
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="certificates">
                    <div className="span12">
                      <BackboneComponent
                        id="field-attachments-container"
                        model={model}
                        view={View.FieldCertificates}
                        getProps={(model: BackboneModel) => ({ model })}
                        onChange={(certificates: []) => setFieldValue("certificates", certificates)}
                      />
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="attributes">
                    <div className="col-4">
                      <BackboneComponent
                        id="field_attributes_content"
                        model={model}
                        view={View.FieldAttributes}
                        getProps={(model: BackboneModel) => ({ model })}
                        onChange={(val: []) => setFieldValue("field_attributes", val)}
                      />
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="soil_type">
                    <div className="row">
                      <div className="col-6 b-r p-4">
                        <LabeledSelect
                          label={gettext("Soil type")}
                          menuPlacement="auto"
                          options={getSoilOptions({
                            country: model.get("country"),
                          })}
                          name="soil_type"
                          placeholder={gettext("Choose a soiltype")}
                        />
                        <LabeledSelect
                          label={gettext("Regulatory soil type")}
                          menuPlacement="auto"
                          options={getSoilOptions({
                            country: model.get("country"),
                            base: cropr.reg_soiltype,
                          })}
                          name="regulatory_soil_type"
                          placeholder={gettext("Choose a regulatory soiltype")}
                        />
                      </div>

                      <div className="col-6 p-4 text-center">
                        {getSoilTriangles({
                          country: model.get("country"),
                        }).map((triangle) => (
                          <SoilTriangleCroprCodes
                            key={triangle}
                            size={250}
                            className="pb-3"
                            selected={values["soil_type"]}
                            onChange={(val) => {
                              setFieldValue("soil_type", val);
                            }}
                            textBreak={false}
                            triangle={triangle}
                          />
                        ))}
                      </div>
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="soil_research">
                    <div className="span12">BLAA</div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="notes">
                    <LabeledField
                      label={gettext("Notes")}
                      name="notes"
                      type="textarea"
                      inputClass="h-320px"
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Modal.Body>
            </Tab.Container>
            <ModelEditModalFooter disabled={isSubmitting} {...props} />
          </form>
        )}
      </ModelSubmitFormik>
    </Modal>
  );
};

export default CropFieldPropertiesModal;
